import { Button, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

import { AddIcon } from "@/assets/icons";
import { useAddAllergyModal } from "@/modals/addAllergy";
import { PatientEncounter, Allergy } from "@/resources/types";
import { css } from "@/theme";
import { useTypes } from "@/providers/TypesProvider";

const styles = {
  table: css`
    .MuiDataGrid-columnHeaders {
      border: none;
    }

    .MuiDataGrid-main > div:nth-of-type(2) {
      overflow: unset !important;
    }

    .MuiDataGrid-virtualScrollerRenderZone {
      width: 100%;

      > :first-of-type {
        border-top: 1px solid rgba(224, 224, 224, 1);
        border-bottom: 1px solid rgba(224, 224, 224, 1);
      }

      > :nth-of-type(even) {
        background-color: #f0f3ff;
      }

      > :nth-of-type(odd) {
        background-color: #fafbff;
      }

      > :last-of-type {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;

        > div {
          border-bottom: none;
        }
      }
    }

    .MuiDataGrid-footerContainer {
      border: none;
      min-height: unset;
      display: none;
    }

    .MuiDataGrid-cell {
      text-transform: capitalize;
    }

    .MuiDataGrid-overlayWrapperInner {
      display: none;
    }
  `,
  addButton: css`
    background: #f0f3ff;
    color: #161b32;
    font-family: "Lato";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    width: 100%;
    margin: 1.5rem 0 1rem 0;
    height: 40px;

    &:hover {
      background: #c3c8de;
    }
  `,
};

const getLabelWhoAddedAllergy = (typeOfUser: Allergy["source"]) => {
  switch (typeOfUser) {
    case "patient":
      return "Patient";
    case "practitioner":
      return "Health Professional";
    default:
      return "Unknown";
  }
};

const getColumns = (allergySeverityOptions: { value: string; label: string }[]): GridColDef[] => [
  { field: "description", headerName: "Allergens", flex: 1 },
  {
    field: "severity",
    headerName: "Reaction",
    flex: 1,
    renderCell: (param) => {
      const option = allergySeverityOptions.find((opt) => opt.value === param.row.severity);
      return option?.label || param.row.severity || "";
    },
  },
  {
    field: "source",
    headerName: "Added by",
    flex: 1,
    renderCell: (param) => getLabelWhoAddedAllergy(param.row.source),
  },
];

const AddAllergyButton = ({ encounter, disabled = false }: { encounter: PatientEncounter; disabled?: boolean }) => {
  const openAddAllergyModal = useAddAllergyModal();

  const handleAddAllergy = () => {
    openAddAllergyModal({ encounter });
  };

  return (
    <Button css={styles.addButton} fullWidth onClick={handleAddAllergy} disabled={disabled}>
      <AddIcon css={{ marginRight: "0.5rem" }} />
      Add Allergen
    </Button>
  );
};

export const PatientAllergies = (props: { encounter: PatientEncounter }) => {
  const { encounter } = props;
  const { allergySeverityOptions } = useTypes();

  // Keep editability consistent with the generic patient form modal.
  // See apps/portal/src/modals/genericForm/genericAddPatientModal.tsx where
  // the `allergies` field is disabled with a tooltip using the same criteria.
  const isBlocked = (
    // !!encounter.triaged || triaged patients are allowed
    encounter.status === "discharged" ||
    encounter.status === "disabled"
  );

  let blockedReason: string | null = null;
  if (encounter.status === "discharged") blockedReason = "Allergies cannot be added to a discharged encounter.";
  if (encounter.status === "disabled") blockedReason = "This encounter is disabled.";

  const rows = (encounter.patient.allergies || []).map((allergy, index) => ({
    id: index + 1,
    ...allergy,
  }));

  const columns = getColumns(allergySeverityOptions);
  const hasAllergies = rows.length > 0;
  const noAllergies = (encounter.patient as any).noAllergies ?? ((encounter.patient.allergies?.length ?? 0) === 0);

  return (
    <div>
      {hasAllergies ? (
        <DataGridPro rows={rows} columns={columns} css={styles.table} />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          {noAllergies ? "Patient has no known allergies" : "No allergies recorded"}
        </Typography>
      )}
      {/* just disable the add allergy button for now - as we might only manage the allergies via the add/edit patient modal */}
      {/* {isBlocked ? (
        <Tooltip title={blockedReason || "Action not available"} placement="top" arrow>
          <span>
            <AddAllergyButton encounter={encounter} disabled />
          </span>
        </Tooltip>
      ) : (
        <AddAllergyButton encounter={encounter} />
      )} */}
    </div>
  );
};
